import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Cloud, Loader2, X } from "lucide-react";

const BATCH = 100;

/**
 * Auto-detects inline base64 assets and offers a one-click migration that
 * loops in 100-asset batches until the backlog is fully cleared.
 */
export default function StorageMigrationBanner() {
  const [audit, setAudit] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState(null); // {done, remaining}
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/storage/audit");
      setAudit(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const inlineTotal = audit?._totals?.inline_b64
    ?? (audit ? Object.entries(audit).filter(([k]) => !k.startsWith("_"))
                       .reduce((acc, [, v]) => acc + (v.inline_b64 || 0), 0) : 0);

  const migrate = async () => {
    setMigrating(true);
    let done = 0;
    let failed = 0;
    try {
      // Loop until backend reports no more remaining
      // Safety: hard-cap at 50 iterations (5000 assets) to avoid runaway
      for (let i = 0; i < 50; i++) {
        const { data } = await api.post(`/storage/migrate?limit=${BATCH}`);
        done += data.total_migrated || 0;
        failed += data.total_failed || 0;
        setProgress({ done, remaining: data.total_remaining || 0 });
        if (!data.has_more) break;
      }
      toast.success(`Migrated ${done} asset${done === 1 ? "" : "s"} to cloud storage`);
      if (failed > 0) toast.error(`${failed} failed; will retry on next run`);
      await load();
    } catch {
      toast.error("Migration paused — try again later");
    } finally {
      setMigrating(false);
      setProgress(null);
    }
  };

  if (!audit || inlineTotal === 0 || dismissed) return null;

  return (
    <div className="glass rounded-2xl p-4 border border-indigo-500/30 bg-indigo-500/[0.04] flex items-center gap-3 flex-wrap" data-testid="storage-migration-banner">
      <Cloud className="h-5 w-5 text-indigo-300 shrink-0" />
      <div className="flex-1 min-w-[220px]">
        <div className="text-sm font-semibold">Optimize storage</div>
        <div className="text-xs text-white/60">
          {migrating && progress
            ? `Migrating… ${progress.done} done · ${progress.remaining} remaining`
            : `${inlineTotal} asset${inlineTotal === 1 ? "" : "s"} still stored inline. Migrate to cloud-served URLs for faster loads.`}
        </div>
      </div>
      <button onClick={migrate} disabled={migrating} data-testid="storage-migrate-btn"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
        {migrating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
        {migrating ? "Working…" : `Migrate ${inlineTotal}`}
      </button>
      <button onClick={() => setDismissed(true)} data-testid="storage-migrate-dismiss"
        className="p-1.5 rounded hover:bg-white/5" title="Dismiss">
        <X className="h-3 w-3 text-white/40" />
      </button>
    </div>
  );
}
